import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import {
    useTheme,
    Typography,
    IconButton,
    Tooltip,
    makeStyles,
    Theme,
    CircularProgress,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import AssessmentIcon from '@material-ui/icons/Assessment'
import { ProjectAnalytics } from '../../../hooks/useEZFolioProject'

const useStyles = makeStyles((theme: Theme) => ({
    tooltip: {
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
    },
}))

interface Props {
    projects: ProjectAnalytics[]
    viewMode: 'serviceOrders' | 'budget'
    loading?: boolean
    onClose?: () => void
    hideUnitReadiness?: boolean
    /** When true, renders inline without overlay header/close controls. */
    embedded?: boolean
}

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value)

export const ProjectAnalyticsCharts: React.FC<Props> = ({
    projects,
    viewMode,
    loading,
    onClose,
    hideUnitReadiness,
    embedded = false,
}) => {
    const theme = useTheme()

    const containerStyle: React.CSSProperties = embedded
        ? {
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              backgroundColor: theme.palette.grey[100],
              padding: theme.spacing(2),
          }
        : {
              position: 'relative',
              flex: 1,
              overflow: 'auto',
              backgroundColor: theme.palette.grey[100],
              boxShadow: `2px -3px 8px ${theme.palette.grey[300]}`,
              padding: theme.spacing(2),
          }

    if (loading) {
        return (
            <div
                style={{
                    ...containerStyle,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!embedded && onClose && (
                    <IconButton
                        onClick={onClose}
                        style={{ position: 'absolute', top: 12, right: 12 }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <CircularProgress
                    size={48}
                    style={{ color: theme.palette.primary.main }}
                />
                <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    style={{ marginTop: theme.spacing(2) }}
                >
                    Loading analytics...
                </Typography>
            </div>
        )
    }

    if (projects.length === 0) {
        return (
            <div
                style={{
                    ...containerStyle,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!embedded && onClose && (
                    <IconButton
                        onClick={onClose}
                        style={{ position: 'absolute', top: 12, right: 12 }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <AssessmentIcon
                    style={{
                        fontSize: 64,
                        color: theme.palette.grey[400],
                        marginBottom: theme.spacing(2),
                    }}
                />
                <Typography variant="h6" color="textSecondary">
                    No project data to visualize
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: theme.spacing(0.5) }}
                >
                    Try adjusting your filters or switching views
                </Typography>
            </div>
        )
    }

    return (
        <div style={containerStyle}>
            {!embedded && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <Typography variant="h6">
                        {viewMode === 'serviceOrders'
                            ? 'Service Order Analytics'
                            : 'Budget Analytics'}
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                            style={{ marginLeft: theme.spacing(1) }}
                        >
                            ({projects.length} projects)
                        </Typography>
                    </Typography>
                    {onClose && (
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            )}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: theme.spacing(2),
                }}
            >
                {viewMode === 'serviceOrders' ? (
                    <ServiceOrderCharts
                        projects={projects}
                        hideUnitReadiness={hideUnitReadiness}
                    />
                ) : (
                    <BudgetCharts projects={projects} />
                )}
            </div>
        </div>
    )
}

const ChartCard: React.FC<{
    title: string
    tooltip: string
    fullWidth?: boolean
    children: React.ReactNode
}> = ({ title, tooltip, fullWidth, children }) => {
    const theme = useTheme()
    const classes = useStyles()
    return (
        <div
            style={{
                gridColumn: fullWidth ? '1 / -1' : undefined,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 8,
                padding: theme.spacing(2),
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(0.5),
                    marginBottom: theme.spacing(1),
                }}
            >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <Tooltip title={tooltip} classes={{ tooltip: classes.tooltip }}>
                    <InfoOutlinedIcon
                        style={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                            cursor: 'help',
                        }}
                    />
                </Tooltip>
            </div>
            {children}
        </div>
    )
}

const MetricCard: React.FC<{
    label: string
    value: string | number
    color?: string
}> = ({ label, value, color }) => {
    const theme = useTheme()
    return (
        <div
            style={{
                textAlign: 'center',
                padding: theme.spacing(1.5),
                backgroundColor: theme.palette.grey[50],
                borderRadius: 6,
                borderLeft: `4px solid ${color || theme.palette.primary.main}`,
            }}
        >
            <Typography variant="body2" color="textSecondary">
                {label}
            </Typography>
            <Typography
                variant="h5"
                style={{ fontWeight: 700, color: color || 'inherit' }}
            >
                {value}
            </Typography>
        </div>
    )
}

// --------------- SERVICE ORDER CHARTS ---------------

const ServiceOrderCharts: React.FC<{
    projects: ProjectAnalytics[]
    hideUnitReadiness?: boolean
}> = ({ projects, hideUnitReadiness }) => {
    const theme = useTheme()

    const totals = useMemo(() => {
        const t = {
            total_workorders: 0,
            not_started: 0,
            in_progress: 0,
            complete: 0,
            approved: 0,
            invoiced: 0,
            behind: 0,
            units_assigned: 0,
            unassigned_units: 0,
            units_need_service: 0,
            units_with_pending_work: 0,
            unique_units: 0,
            units_ready_count: 0,
            units_ready_scope_count: 0,
        }
        projects.forEach((p) => {
            t.total_workorders += p.total_workorders
            t.not_started += p.not_started
            t.in_progress += p.in_progress_count
            t.complete += p.complete_count
            t.approved += p.approved_count
            t.invoiced += p.invoiced_count
            t.behind += p.behind_count
            t.units_assigned += p.units_assigned
            t.unassigned_units += p.unassigned_units
            t.units_need_service += p.units_need_service
            t.units_with_pending_work += p.units_with_pending_work
            t.unique_units += p.unique_units
            const readyScope =
                p.units_ready_scope_count ??
                p.units_need_service + p.unique_units - p.units_assigned
            const readyCount =
                p.units_ready_count ??
                readyScope - p.unassigned_units - p.units_with_pending_work
            t.units_ready_count += readyCount
            t.units_ready_scope_count += readyScope
        })
        return t
    }, [projects])

    const completionPct =
        totals.total_workorders > 0
            ? Math.round(
                  ((totals.complete + totals.approved + totals.invoiced) /
                      totals.total_workorders) *
                      100,
              )
            : 0

    const onTrackPct =
        totals.total_workorders > 0
            ? Math.round(
                  ((totals.total_workorders - totals.behind) /
                      totals.total_workorders) *
                      100,
              )
            : 0

    const pipelineDonutOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: [
            'Not Started',
            'In Progress',
            'Complete',
            'Approved',
            'Invoiced',
        ],
        colors: [
            theme.palette.grey[400],
            theme.palette.info.main,
            theme.palette.success.main,
            '#7c4dff',
            '#00bfa5',
        ],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${val.toFixed(1)}%`,
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total WOs',
                            fontSize: '14px',
                            fontWeight: '600',
                            formatter: () =>
                                totals.total_workorders.toLocaleString(),
                        },
                    },
                },
            },
        },
    }

    const pipelineDonutSeries = [
        totals.not_started,
        totals.in_progress,
        totals.complete,
        totals.approved,
        totals.invoiced,
    ]

    const completionRadialOptions: ApexOptions = {
        chart: { type: 'radialBar' },
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: { size: '60%' },
                track: { background: theme.palette.grey[200] },
                dataLabels: {
                    name: { fontSize: '14px', offsetY: -10 },
                    value: {
                        fontSize: '28px',
                        fontWeight: '700',
                        offsetY: 5,
                        formatter: (val: number) => `${val}%`,
                    },
                },
            },
        },
        labels: ['Completion Rate'],
        colors: [
            completionPct >= 75
                ? theme.palette.success.main
                : completionPct >= 40
                ? theme.palette.warning.main
                : theme.palette.error.main,
        ],
    }

    const statusBarOptions: ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: true, barHeight: '50%', distributed: true },
        },
        xaxis: { labels: { style: { fontSize: '12px' } } },
        yaxis: { labels: { style: { fontSize: '12px' } } },
        colors: [
            theme.palette.grey[400],
            theme.palette.info.main,
            theme.palette.success.main,
            '#7c4dff',
            '#00bfa5',
        ],
        legend: { show: false },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontWeight: '600' },
        },
        tooltip: {
            y: { formatter: (val: number) => `${val} workorders` },
        },
    }

    const statusBarSeries = [
        {
            name: 'Count',
            data: [
                { x: 'Not Started', y: totals.not_started },
                { x: 'In Progress', y: totals.in_progress },
                { x: 'Complete', y: totals.complete },
                { x: 'Approved', y: totals.approved },
                { x: 'Invoiced', y: totals.invoiced },
            ],
        },
    ]

    const behindDonutOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: ['On Track', 'Behind'],
        colors: [theme.palette.success.main, theme.palette.error.main],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${val.toFixed(1)}%`,
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total WOs',
                            fontSize: '14px',
                            fontWeight: '600',
                        },
                    },
                },
            },
        },
    }

    const behindDonutSeries = [
        totals.total_workorders - totals.behind,
        totals.behind,
    ]

    const totalUnits = totals.units_assigned + totals.unassigned_units
    const unitsPieOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: ['Assigned', 'Unassigned'],
        colors: [theme.palette.success.main, theme.palette.grey[400]],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${val.toFixed(1)}%`,
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total Units',
                            fontSize: '14px',
                            fontWeight: '600',
                        },
                    },
                },
            },
        },
    }

    const unitsPieSeries = [totals.units_assigned, totals.unassigned_units]

    const unitsReady = totals.units_ready_count
    const unitsReadyPct =
        totals.units_ready_scope_count > 0
            ? Math.round((unitsReady / totals.units_ready_scope_count) * 100)
            : 0

    const unitsReadyRadialOptions: ApexOptions = {
        chart: { type: 'radialBar' },
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: { size: '60%' },
                track: { background: theme.palette.grey[200] },
                dataLabels: {
                    name: { fontSize: '13px', offsetY: -10 },
                    value: {
                        fontSize: '24px',
                        fontWeight: '700',
                        offsetY: 5,
                        formatter: (val: number) => `${val}%`,
                    },
                },
            },
        },
        labels: ['Units Ready'],
        colors: [
            unitsReadyPct >= 75
                ? theme.palette.success.main
                : unitsReadyPct >= 40
                ? theme.palette.warning.main
                : theme.palette.error.main,
        ],
    }

    const unitsReadyBarOptions: ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: true, barHeight: '50%', distributed: true },
        },
        xaxis: { labels: { style: { fontSize: '12px' } } },
        yaxis: { labels: { style: { fontSize: '12px' } } },
        colors: [
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.info.main,
        ],
        legend: { show: false },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontWeight: '600' },
        },
    }

    const unitsReadyBarSeries = [
        {
            name: 'Units',
            data: [
                { x: 'Ready', y: unitsReady },
                { x: 'Pending Work', y: totals.units_with_pending_work },
                { x: 'Unassigned (Need Service)', y: totals.unassigned_units },
                {
                    x: 'In Scope (Denominator)',
                    y: totals.units_ready_scope_count,
                },
            ],
        },
    ]

    return (
        <>
            {/* Summary Metrics Row */}
            <div
                style={{
                    gridColumn: '1 / -1',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: theme.spacing(2),
                }}
            >
                <MetricCard
                    label="Total Workorders"
                    value={totals.total_workorders.toLocaleString()}
                    color={theme.palette.primary.main}
                />
                <MetricCard
                    label="Behind Schedule"
                    value={totals.behind.toLocaleString()}
                    color={theme.palette.error.main}
                />
                <MetricCard
                    label="Total Units"
                    value={totalUnits.toLocaleString()}
                    color={theme.palette.info.main}
                />
                <MetricCard
                    label="Units Ready"
                    value={`${unitsReadyPct}%`}
                    color={
                        unitsReadyPct >= 75
                            ? theme.palette.success.main
                            : unitsReadyPct >= 40
                            ? theme.palette.warning.main
                            : theme.palette.error.main
                    }
                />
                <MetricCard
                    label="On Track"
                    value={`${onTrackPct}%`}
                    color={theme.palette.success.main}
                />
            </div>

            <ChartCard
                title="Workorder Pipeline"
                tooltip="Distribution of all workorders across status stages: Not Started, In Progress, Complete, Approved, Invoiced, and Behind"
            >
                <ReactApexChart
                    options={pipelineDonutOptions}
                    series={pipelineDonutSeries}
                    type="donut"
                    height={320}
                />
            </ChartCard>

            <ChartCard
                title="Overall Completion Rate"
                tooltip="Percentage of workorders that are Complete, Approved, or Invoiced out of total workorders across all projects"
            >
                <ReactApexChart
                    options={completionRadialOptions}
                    series={[completionPct]}
                    type="radialBar"
                    height={320}
                />
            </ChartCard>

            <ChartCard
                title="Workorder Status Totals"
                tooltip="Total count of workorders in each status category across all projects"
                fullWidth
            >
                <ReactApexChart
                    options={statusBarOptions}
                    series={statusBarSeries}
                    type="bar"
                    height={280}
                />
            </ChartCard>

            <ChartCard
                title="Behind vs On Track"
                tooltip="Proportion of workorders that are behind schedule versus those that are on track across all projects"
            >
                <ReactApexChart
                    options={behindDonutOptions}
                    series={behindDonutSeries}
                    type="donut"
                    height={300}
                />
            </ChartCard>

            <ChartCard
                title="Unit Assignment Summary"
                tooltip="How many units have been assigned to a vendor versus units that still need to be assigned across all projects"
            >
                <ReactApexChart
                    options={unitsPieOptions}
                    series={unitsPieSeries}
                    type="donut"
                    height={300}
                />
            </ChartCard>

            <ChartCard
                title="Units Ready"
                tooltip="Of units that need service plus any unit with a work order, the % that are ready (no required-but-missing work order and no active work orders)"
            >
                <ReactApexChart
                    options={unitsReadyRadialOptions}
                    series={[unitsReadyPct]}
                    type="radialBar"
                    height={300}
                />
            </ChartCard>

            <ChartCard
                title="Unit Readiness Breakdown"
                tooltip="Ready count, units with active work orders, unassigned units that need service, and total in-scope denominator"
            >
                <ReactApexChart
                    options={unitsReadyBarOptions}
                    series={unitsReadyBarSeries}
                    type="bar"
                    height={200}
                />
            </ChartCard>
        </>
    )
}

// --------------- BUDGET CHARTS ---------------

const BudgetCharts: React.FC<{
    projects: ProjectAnalytics[]
}> = ({ projects }) => {
    const theme = useTheme()

    const totals = useMemo(() => {
        const t = {
            baseline_budget: 0,
            total_cost: 0,
            approved_co_cost: 0,
            pending_co_cost: 0,
            approved_co_count: 0,
            pending_co_count: 0,
            total_units: 0,
            total_bed_spaces: 0,
        }
        projects.forEach((p) => {
            t.baseline_budget += p.baseline_budget
            t.total_cost += p.total_cost
            t.approved_co_cost += p.approved_change_order_cost
            t.pending_co_cost += p.pending_change_order_cost
            t.approved_co_count += p.approved_change_orders
            t.pending_co_count += p.pending_change_orders
            t.total_units += p.unique_units
            t.total_bed_spaces += p.unique_occupiable_areas
        })
        return t
    }, [projects])

    const variance = totals.baseline_budget - totals.total_cost
    const variancePct =
        totals.baseline_budget > 0
            ? Math.round((variance / totals.baseline_budget) * 100)
            : 0
    const avgCostPerUnit =
        totals.total_units > 0 ? totals.total_cost / totals.total_units : 0
    const avgCostPerBed =
        totals.total_bed_spaces > 0
            ? totals.total_cost / totals.total_bed_spaces
            : 0

    const budgetCompareOptions: ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: false, columnWidth: '45%', distributed: true },
        },
        xaxis: {
            categories: ['Baseline Budget', 'Forecasted Cost'],
            labels: { style: { fontSize: '13px', fontWeight: '600' } },
        },
        yaxis: {
            labels: { formatter: (val: number) => formatCurrency(val) },
        },
        colors: [theme.palette.primary.main, theme.palette.warning.main],
        legend: { show: false },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => formatCurrency(val),
            style: { fontSize: '12px' },
        },
        tooltip: {
            y: { formatter: (val: number) => formatCurrency(val) },
        },
    }

    const budgetCompareSeries = [
        {
            name: 'Amount',
            data: [totals.baseline_budget, totals.total_cost],
        },
    ]

    const varianceRadialOptions: ApexOptions = {
        chart: { type: 'radialBar' },
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: { size: '60%' },
                track: { background: theme.palette.grey[200] },
                dataLabels: {
                    name: { fontSize: '13px', offsetY: -10 },
                    value: {
                        fontSize: '24px',
                        fontWeight: '700',
                        offsetY: 5,
                        formatter: () => formatCurrency(variance),
                    },
                },
            },
        },
        labels: [`${variancePct >= 0 ? 'Under' : 'Over'} Budget`],
        colors: [
            variance >= 0
                ? theme.palette.success.main
                : theme.palette.error.main,
        ],
    }

    const costBreakdownOptions: ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: true, barHeight: '50%', distributed: true },
        },
        xaxis: {
            labels: {
                formatter: (val: string) => formatCurrency(Number(val)),
            },
        },
        yaxis: {
            labels: { style: { fontSize: '12px' } },
        },
        colors: [
            theme.palette.primary.main,
            theme.palette.info.main,
            '#00bfa5',
        ],
        legend: { show: false },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => formatCurrency(val),
            style: { fontSize: '11px' },
        },
        tooltip: {
            y: { formatter: (val: number) => formatCurrency(val) },
        },
    }

    const costBreakdownSeries = [
        {
            name: 'Amount',
            data: [
                { x: 'Avg Cost / Unit', y: avgCostPerUnit },
                { x: 'Avg Cost / Bed Space', y: avgCostPerBed },
            ],
        },
    ]

    const changeOrderDonutOptions: ApexOptions = {
        chart: { type: 'donut' },
        labels: ['Approved CO Cost', 'Pending CO Cost'],
        colors: [theme.palette.success.main, theme.palette.warning.main],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: {
            enabled: true,
            formatter: (val: number, opts: any) => {
                const idx = opts.seriesIndex
                const raw =
                    idx === 0 ? totals.approved_co_cost : totals.pending_co_cost
                return formatCurrency(raw)
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total CO Cost',
                            fontSize: '13px',
                            fontWeight: '600',
                            formatter: () =>
                                formatCurrency(
                                    totals.approved_co_cost +
                                        totals.pending_co_cost,
                                ),
                        },
                    },
                },
            },
        },
    }

    const changeOrderDonutSeries = [
        totals.approved_co_cost,
        totals.pending_co_cost,
    ]

    return (
        <>
            {/* Summary Metrics Row */}
            <div
                style={{
                    gridColumn: '1 / -1',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: theme.spacing(2),
                }}
            >
                <MetricCard
                    label="Total Budget"
                    value={formatCurrency(totals.baseline_budget)}
                    color={theme.palette.primary.main}
                />
                <MetricCard
                    label="Forecasted Cost"
                    value={formatCurrency(totals.total_cost)}
                    color={theme.palette.warning.main}
                />
                <MetricCard
                    label="Variance"
                    value={formatCurrency(variance)}
                    color={
                        variance >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main
                    }
                />
                <MetricCard
                    label="Change Orders"
                    value={`${
                        totals.approved_co_count + totals.pending_co_count
                    }`}
                    color={theme.palette.info.main}
                />
            </div>

            <ChartCard
                title="Budget vs Forecasted Cost"
                tooltip="Side-by-side comparison of the total baseline budget versus the total forecasted cost across all projects"
            >
                <ReactApexChart
                    options={budgetCompareOptions}
                    series={budgetCompareSeries}
                    type="bar"
                    height={320}
                />
            </ChartCard>

            <ChartCard
                title="Budget Health"
                tooltip="How much of the total budget has been consumed. Green means under budget, red means over budget. The dollar amount shows the total variance."
            >
                <ReactApexChart
                    options={varianceRadialOptions}
                    series={[Math.min(Math.abs(variancePct), 100)]}
                    type="radialBar"
                    height={320}
                />
            </ChartCard>

            <ChartCard
                title="Cost Breakdown"
                tooltip="Average cost per unit (Forecasted Cost / Units Assigned) and average cost per bed space (Forecasted Cost / Bed Spaces) across all projects"
                fullWidth
            >
                <ReactApexChart
                    options={costBreakdownOptions}
                    series={costBreakdownSeries}
                    type="bar"
                    height={200}
                />
            </ChartCard>

            <ChartCard
                title="Change Orders: Approved vs Pending"
                tooltip="Total cost of approved change orders versus pending change orders across all projects. The center shows the combined total."
                fullWidth
            >
                <ReactApexChart
                    options={changeOrderDonutOptions}
                    series={changeOrderDonutSeries}
                    type="donut"
                    height={320}
                />
            </ChartCard>
        </>
    )
}
